import { cn } from "@/utils";
import { canvasToolbarIconClassName } from "../../components/toolbar/canvasToolbarIcon";
import {
  canvasToolbarColorSwatchClassName,
  canvasToolbarColorSwatchNoneClassName,
} from "../../components/toolbar/canvasToolbarStyles";
import type { ShapeStrokeStyle } from "../../hooks/useCanvasShapeAndEdgeStyle";

const STROKE_DASH: Record<
  Exclude<ShapeStrokeStyle, "none">,
  string | undefined
> = {
  solid: undefined,
  dashed: "4 2",
  dotted: "1 2",
};

/** Maps stored stroke widths to preview weights inside a 16×16 viewBox. */
const WEIGHT_PREVIEW: Record<number, number> = {
  1: 1,
  2: 1.5,
  4: 2.5,
};

/** Squiggles carry a little more weight than flat lines to stay readable. */
const SQUIGGLE_PREVIEW: Record<number, number> = {
  1: 1.25,
  2: 2,
  4: 3.25,
};

/**
 * What a picker sets, so the swatch can draw it (design-system experiment
 * 19, `Swatch` in picker.jsx): a fill is a tinted disc, a mark on a line or
 * an outline is a ring, a mark on text is the letter A, a Section is the
 * tint with its dark border around it.
 */
export type CanvasSwatchRole = "fill" | "mark" | "text" | "section";

interface CanvasToolbarColorSwatchProps {
  /** The tint (fill, section) — or, without a role, the kit's centre. */
  fill?: string;
  /** The dark stop (section) — or, without a role, the kit's ring. */
  stroke?: string;
  /** The mark (mark, text). */
  mark?: string;
  /** Absent: the one-recipe tint-and-ring swatch the legacy bar draws. */
  role?: CanvasSwatchRole;
  className?: string;
}

function isNoneSwatchFill(fill: string | undefined): boolean {
  return fill === "transparent" || fill === "none";
}

/** Circular color swatch — matches specs/canvas-toolbars ColorRow / `.toolbar-color-swatch`. */
export function CanvasToolbarColorSwatch({
  fill = "transparent",
  stroke = "var(--n-90)",
  mark = "var(--n-90)",
  role,
  className,
}: CanvasToolbarColorSwatchProps) {
  if (role === "text") {
    return (
      <span
        className={cn(
          "inline-grid size-3.5 shrink-0 place-items-center font-sans text-[length:var(--text-base)] leading-none font-semibold",
          className,
        )}
        style={{ color: mark }}
        aria-hidden
      >
        A
      </span>
    );
  }
  if (role === "mark") {
    return (
      <span
        className={cn(canvasToolbarColorSwatchClassName, className)}
        style={{
          background: "transparent",
          borderColor: mark,
          borderWidth: "2.5px",
        }}
        aria-hidden
      />
    );
  }
  if (isNoneSwatchFill(fill)) {
    return (
      <span
        className={cn(canvasToolbarColorSwatchNoneClassName, className)}
        aria-hidden
      />
    );
  }
  if (role === "fill") {
    return (
      <span
        className={cn(canvasToolbarColorSwatchClassName, className)}
        style={{
          background: fill,
          borderColor: "color-mix(in oklab, var(--n-90) 22%, transparent)",
        }}
        aria-hidden
      />
    );
  }
  if (role === "section") {
    return (
      <span
        className={cn(canvasToolbarColorSwatchClassName, className)}
        style={{ background: fill, borderColor: stroke, borderWidth: "2px" }}
        aria-hidden
      />
    );
  }

  return (
    <span
      className={cn(canvasToolbarColorSwatchClassName, className)}
      style={{ background: fill, borderColor: stroke }}
      aria-hidden
    />
  );
}

interface CanvasStrokeTypePreviewProps {
  strokeStyle: ShapeStrokeStyle;
  active?: boolean;
  className?: string;
}

/** Horizontal stroke pattern preview — 16×16, matches other toolbar glyphs. */
export function CanvasStrokeTypePreview({
  strokeStyle,
  active = false,
  className,
}: CanvasStrokeTypePreviewProps) {
  const color = active ? "var(--rust)" : "var(--n-90)";

  if (strokeStyle === "none") {
    return (
      <svg
        className={cn(canvasToolbarIconClassName, className)}
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden
      >
        <line
          x1="2"
          y1="14"
          x2="14"
          y2="2"
          stroke="var(--n-30)"
          strokeWidth="1"
        />
      </svg>
    );
  }

  return (
    <svg
      className={cn(canvasToolbarIconClassName, className)}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <line
        x1="2"
        y1="8"
        x2="14"
        y2="8"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray={STROKE_DASH[strokeStyle]}
      />
    </svg>
  );
}

interface CanvasStrokeWidthPreviewProps {
  width: number;
  active?: boolean;
  className?: string;
}

/** Solid line weight preview — 16×16, matches other toolbar glyphs. */
export function CanvasStrokeWidthPreview({
  width,
  active = false,
  className,
}: CanvasStrokeWidthPreviewProps) {
  const color = active ? "var(--rust)" : "var(--n-90)";
  const previewWidth = WEIGHT_PREVIEW[width] ?? width;

  return (
    <svg
      className={cn(canvasToolbarIconClassName, className)}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <line
        x1="2"
        y1="8"
        x2="14"
        y2="8"
        stroke={color}
        strokeWidth={previewWidth}
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Wavy stroke sample for the pen's weight row — 16×16, same box as the lines. */
export function CanvasStrokeSquigglePreview({
  width,
  active = false,
  className,
}: CanvasStrokeWidthPreviewProps) {
  const color = active ? "var(--rust)" : "var(--n-90)";
  const previewWidth = SQUIGGLE_PREVIEW[width] ?? width;

  return (
    <svg
      className={cn(canvasToolbarIconClassName, className)}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M2 10.4C3.4 5.9 5.2 5.6 6.8 8.2c1.6 2.6 3.4 2.3 4.8-.6 0 0 1.2-2 2.4-1.4"
        stroke={color}
        strokeWidth={previewWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
